import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { dashboardFor } from "@/components/ProtectedRoute";

export default function Login() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== false) navigate(dashboardFor(user.role), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate(dashboardFor(res.user.role), { replace: true });
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#0A0A0B]" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative fl-grid-bg overflow-hidden">
        <img src="https://images.unsplash.com/photo-1708011108832-6c5883f4558a?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Athletes" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 to-[#E53E3E]/20" />
        <div className="relative p-16 flex flex-col justify-end">
          <h2 className="font-display text-5xl font-black uppercase tracking-tight leading-[0.95]">More Than a Workout.<br /><span className="text-[#E53E3E]">A Stronger You.</span></h2>
          <p className="mt-4 text-zinc-300 max-w-md">Log back in and keep your streak alive.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-8"><ArrowLeft className="w-4 h-4" /> Back to home</Link>
          <Logo size="h-9" className="mb-8" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Welcome back</h1>
          <p className="text-zinc-400 mt-2">Log in to your FitLynk account.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-[#E53E3E]" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-12 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold" data-testid="login-submit-button">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log In"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-zinc-400 text-center">
            New to FitLynk? <Link to="/get-started" className="text-[#E53E3E] font-semibold hover:underline">Get Started</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
